import { useDispatch, useSelector } from "react-redux";
import { StoreProptypes } from "../../PropTypes/StoreProptypes";
import { useTranslation } from "react-i18next";
import { Button, Card, Col, Form, InputGroup, Modal, Row, Badge } from "react-bootstrap";

import "../../Assets/scss/pages/settings.scss";
import NoRecordFound from "../../Components/NoRecordFound";
import { useEffect, useMemo, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { useLocation, useNavigate } from "react-router-dom";
import { AddVoiceCommandService, deleteVoiceCommandService, getVoiceCommandService, UpdateVoiceCommandService } from "../../Services/VoiceCommandService";

interface PropTypes {
    disabledButton: boolean
}

// component to render settings page
const VoiceCommandPage = (prop: PropTypes) => {
  // Redux Data
  const { accessToken, currentWebsite, currentUserStatus } = useSelector(
    (store: StoreProptypes) => store
  );

  const { disabledButton } = prop;

  // Functions
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // useState
  const [voiceCommandListData, setVoiceCommandListData] = useState<any>();
  const location = useLocation();
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [showAliasModal, setShowAliasModal] = useState(false);
  const [selectedAliases, setSelectedAliases] = useState<string[]>([]);
  
  useEffect(() => {
    if (accessToken && currentWebsite) {
      getVoiceCommandService(accessToken, currentWebsite?.id?.toString(), setVoiceCommandListData, dispatch, t);
    }
  }, [accessToken, currentWebsite]);

  // Show Card Popup
  const [voiceCommandTitle, setVoiceCommandTitle] = useState("");
  const [voiceCommandDescription, setVoiceCommandDescription] = useState<string[]>([""]);
  const [voiceCommandURL, setVoiceCommandURL] = useState("");
  const [editingVoiceCommand, setEditingVoiceCommand] = useState<any | null>(null);
  const [showVoiceCommandPopup, setShowVoiceCommandPopup] = useState(false);
  const [showDeleteVoiceCommandPopup, setDeleteVoiceCommandPopup] = useState(false);
  const [deleteVoiceCommandID, setDeleteVoiceCommandID] = useState<number | null>();

  // function to close Voice command popup
  const handleCloseVoiceCommandPopup = () => {
    setShowVoiceCommandPopup(false);
    resetVoiceCommandForm()
  };

  // function to show Voice Command popup
  const handleShowVoiceCommandPopup = () => {
    setShowVoiceCommandPopup(true);
  };

  // function to display delete card popup
  const handleShowDeleteVoiceCommandPopup = (id: number) => {
    setDeleteVoiceCommandPopup(true);
    id && setDeleteVoiceCommandID(id);
  };

  const validateVoiceCommand = () => {
    const newErrors: { [key: string]: string } = {};

    if (!voiceCommandTitle.trim()) {
      newErrors.voiceCommandTitle = t("Title is required");
    } else if (voiceCommandTitle.length > 100) {
      newErrors.voiceCommandTitle = t("Title cannot exceed 100 characters");
    }

    if (voiceCommandDescription.length <= 0 || voiceCommandDescription.every(item => !item.trim())) {
      newErrors.alias = t("At least one alias is required");
    }

    if (!voiceCommandURL.trim()) {
      newErrors.voiceCommandURL = t("URL is required");
    } else if (voiceCommandURL.length > 100) {
      newErrors.voiceCommandURL = t("URL cannot exceed 100 characters");
    } else if (!isValidUrl(voiceCommandURL)) {
      newErrors.voiceCommandURL = t("Please enter a valid URL");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const resetVoiceCommandForm = () => {
    setVoiceCommandTitle("");
    setVoiceCommandDescription([""]);
    setVoiceCommandURL("");
    setEditingVoiceCommand(null);
    setErrors({});
  };

  const handleEditVoiceCommand = (id: number) => {
    const getSelectedIdData = voiceCommandListData.find((d: any) => d.id === id)
    if (getSelectedIdData) {
      setVoiceCommandTitle(getSelectedIdData?.command_title)
      setVoiceCommandURL(getSelectedIdData?.command_value)
      setVoiceCommandDescription(getSelectedIdData?.description ?? [""])
      setEditingVoiceCommand(getSelectedIdData)
      handleShowVoiceCommandPopup()
    }
  }

  const saveVoiceNavigationCommand = (e: React.FormEvent) => {
    e.preventDefault();
    if (accessToken && (validateVoiceCommand()) && currentWebsite) {
      if (editingVoiceCommand) {
        UpdateVoiceCommandService(editingVoiceCommand.id, currentWebsite?.id?.toString(), voiceCommandTitle, voiceCommandDescription.join(','), voiceCommandURL, accessToken, setVoiceCommandListData, setShowVoiceCommandPopup, dispatch, t, resetVoiceCommandForm)
      } else {
        AddVoiceCommandService(currentWebsite?.id?.toString(), voiceCommandTitle, voiceCommandDescription.join(','), voiceCommandURL, accessToken, setVoiceCommandListData, setShowVoiceCommandPopup, dispatch, t, resetVoiceCommandForm)
      }
    }
  }

  const handleShowAllAlias = (aliases: string[]) => {
    setSelectedAliases(aliases);
    setShowAliasModal(true);
  };
  
  const handleChange = (value: string, index: number) => {
    const updated = [...voiceCommandDescription];
    updated[index] = value;
    setVoiceCommandDescription(updated);
  };

  const handleAdd = () => {
    if (voiceCommandDescription.length < 10) {
      setVoiceCommandDescription([...voiceCommandDescription, ""]);
    }
  };

  const handleDelete = (index: number) => {
    const updated = voiceCommandDescription.filter((_, i) => i !== index);
    setVoiceCommandDescription(updated.length > 0 ? updated : [""]);
  };

  const handleCloseDeleteVoiceCommandPopup = () => {
    setDeleteVoiceCommandPopup(false);
    setDeleteVoiceCommandID(null);
  };

  const handleDeleteVoiceCommand = () => {
    if (accessToken && deleteVoiceCommandID && currentWebsite) {
      deleteVoiceCommandService(
        accessToken,
        currentWebsite?.id?.toString(),
        deleteVoiceCommandID,
        setVoiceCommandListData,
        setDeleteVoiceCommandPopup,
        dispatch,
        t
      );
    }
  };

  return (
    <>
      <div className="voice-command-page">
        {/* Header Section */}
        <div className="voice-command-header mb-3">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h5 className="mb-1 fw-semibold">{t("Voice Commands")}</h5>
              <p className="text-muted mb-0 small">
                {t("Create custom voice commands to navigate your website")}
              </p>
            </div>
            <Button
              variant="primary"
              size="sm"
              disabled={disabledButton}
              onClick={handleShowVoiceCommandPopup}
              className="d-flex align-items-center gap-2 rounded-3 shadow-sm"
              style={{ fontSize: 12, fontWeight: 500 }}
            >
              <i className="material-symbols-outlined d-flex align-items-center" style={{ fontSize: 20 }}>
                add_circle
              </i>
              <span className="d-flex align-items-center">{t("Add Command")}</span>
            </Button>
          </div>
        </div>

        {/* Commands Grid */}
        <div className="voice-commands-grid">
          {voiceCommandListData ? (
            <>
              {voiceCommandListData.length > 0 ? (
                <Row xs={1} md={2} lg={2} xl={3} className="g-4">
                  {voiceCommandListData.map((voiceItem: any, index: number) => {
                    const colors = ['primary', 'success', 'info', 'warning', 'danger'];
                    const colorIndex = index % colors.length;
                    
                    return (
                      <Col key={voiceItem.id}>
                        <div className="voice-command-card h-100 border-1 border border-dark shadow-sm rounded-3 overflow-hidden">
                          <div className="card-gradient" style={{
                            height: 4,
                            background: `linear-gradient(90deg, var(--bs-${colors[colorIndex]}) 0%, var(--bs-${colors[colorIndex]}-light) 100%)`
                          }} />
                          <div className="card-body p-2">
                            {/* Title and Actions */}
                            <div className="d-flex justify-content-between align-items-start">
                              <div className="flex-grow-1 me-3 w-25">
                                <h6 className="mb-2 fw-semibold text-dark">
                                  {voiceItem.command_title}
                                </h6>
                                {voiceItem.description && voiceItem.description.length > 0 && (
                                  <div className="alias-container">
                                    {/* <i className="material-symbols-outlined me-1" style={{ fontSize: 14, color: '#6c757d' }}>
                                      alternative_email
                                    </i> */}
                                    <span className="alias-text small text-muted">
                                      {voiceItem.description.slice(0, 2).join(", ")}
                                    </span>
                                    {voiceItem.description.length > 2 && (
                                      <Badge
                                        pill
                                        bg="light"
                                        text="dark"
                                        className="ms-2 cursor-pointer"
                                        style={{ cursor: 'pointer', fontSize: 11 }}
                                        onClick={() => handleShowAllAlias(voiceItem.description)}
                                      >
                                        +{voiceItem.description.length - 2} more
                                      </Badge>
                                    )}
                                  </div>
                                )}
                              </div>
                              <div className="action-buttons d-flex gap-2">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  disabled={disabledButton}
                                  aria-label={t("Edit")}
                                  onClick={() => handleEditVoiceCommand(voiceItem.id)}
                                  className="rounded-circle p-2 d-flex align-items-center justify-content-center"
                                  style={{ width: 32, height: 32 }}
                                >
                                  <i className="material-symbols-outlined" style={{ fontSize: 18 }}>
                                    edit
                                  </i>
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  aria-label={t("Delete")}
                                  disabled={disabledButton}
                                  onClick={() => handleShowDeleteVoiceCommandPopup(voiceItem.id)}
                                  className="rounded-circle p-2 d-flex align-items-center justify-content-center"
                                  style={{ width: 32, height: 32 }}
                                >
                                  <i className="material-symbols-outlined" style={{ fontSize: 18 }}>
                                    delete
                                  </i>
                                </Button>
                              </div>
                            </div>

                            {/* URL Section */}
                            {/* <div className="url-section mt-3 pt-2">
                              <div className="d-flex align-items-center gap-2">
                                <i className="material-symbols-outlined text-muted" style={{ fontSize: 16 }}>
                                  link
                                </i>
                                <div className="url-text small text-truncate flex-grow-1" style={{ color: '#0d6efd' }}>
                                  {voiceItem.command_value}
                                </div>
                              </div>
                            </div> */}
                          </div>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              ) : (
                <div className="empty-state text-center py-1">
                  <div className="empty-state-icon mb-2">
                    <i className="material-symbols-outlined" style={{ fontSize: 50, color: '#dee2e6' }}>
                      keyboard_voice
                    </i>
                  </div>
                  <h5 className="text-muted mb-2">{t("No Voice Commands Yet")}</h5>
                  <p className="text-muted small mb-3">
                    {t("Create your first voice command to get started")}
                  </p>
                  {/* <center> */}
                  {/* <Button
                    variant="primary"
                    size="sm"
                    style={{fontSize: 12 }}
                    onClick={handleShowVoiceCommandPopup}
                    className="rounded-pill d-flex align-items-center justify-content-center"
                  >
                    <i className="material-symbols-outlined me-1" style={{ fontSize: 16 }}>
                      add
                    </i>
                    {t("Add Command")}
                  </Button>
                  </center> */}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Modal
        show={showVoiceCommandPopup}
        onHide={handleCloseVoiceCommandPopup}
        size="lg"
        backdrop="static"
        centered
        className="voice-command-modal"
      >
        <Modal.Header closeButton className="border-0 pt-4 px-4">
          <Modal.Title className="fs-4 fw-semibold">
            {editingVoiceCommand ? (
              <div className="d-flex align-items-center gap-2">
                <i className="material-symbols-outlined text-primary">edit_note</i>
                {t("Update Voice Command")}
              </div>
            ) : (
              <div className="d-flex align-items-center gap-2">
                <i className="material-symbols-outlined text-primary">add_circle</i>
                {t("Add New Voice Command")}
              </div>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4 pb-4">
          <Form onSubmit={saveVoiceNavigationCommand}>
            <div className="form-container">
              {/* Command Title */}
              <div className="form-group mb-4">
                <Form.Label className="fw-semibold mb-2">
                  {t("Command Title")}
                  <span className="text-danger ms-1">*</span>
                </Form.Label>
                <InputGroup>
                  <InputGroup.Text className="bg-light border-end-0">
                    <i className="material-symbols-outlined" style={{ fontSize: 20, color: '#6c757d' }}>
                      title
                    </i>
                  </InputGroup.Text>
                  <Form.Control
                    placeholder={t("Enter command name (e.g., 'Go to Home')")}
                    aria-label={t("Title")}
                    type="text"
                    className="border-start-0"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setVoiceCommandTitle(e.target.value)}
                    value={voiceCommandTitle}
                    isInvalid={!!errors.voiceCommandTitle}
                    style={{ paddingRight: "0.75rem" }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.voiceCommandTitle}
                  </Form.Control.Feedback>
                </InputGroup>
              </div>

              {/* Alias Section */}
              <div className="form-group mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Form.Label className="fw-semibold mb-0">
                    {t("Aliases")}
                    <span className="text-danger ms-1">*</span>
                  </Form.Label>
                  {voiceCommandDescription.length < 10 && (
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={handleAdd}
                      className="d-flex align-items-center gap-1 rounded-pill px-3"
                    >
                      <i className="material-symbols-outlined" style={{ fontSize: 16 }}>add</i>
                      {t("Add Alias")}
                    </Button>
                  )}
                </div>
                <Form.Group className="aliases-list">
                  {voiceCommandDescription.map((item, index) => (
                    <div className="alias-input-group mb-2" key={index}>
                      <div className="d-flex align-items-center gap-2">
                        <div className="position-relative flex-grow-1">
                          <Form.Control
                            size="lg"
                            type="text"
                            placeholder={t("Enter alias (e.g., 'Home page', 'Main')")}
                            value={item}
                            onChange={(e) => handleChange(e.target.value, index)}
                            className={`${errors.alias && index === 0 ? 'is-invalid' : ''}`}
                          />
                          {errors.alias && index === 0 && (
                            <div className="invalid-feedback d-block">
                              {errors.alias}
                            </div>
                          )}
                        </div>
                        {voiceCommandDescription.length > 1 && (
                          <Button
                            variant="ghost"
                            size="lg"
                            onClick={() => handleDelete(index)}
                            aria-label={t("Delete Alias")}
                            className="text-danger p-1"
                            style={{ background: 'none', border: 'none' }}
                          >
                            <i className="material-symbols-outlined">close</i>
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </Form.Group>
                <small className="text-muted d-block mt-2">
                  <i className="material-symbols-outlined me-1" style={{ fontSize: 14, verticalAlign: 'middle' }}>
                    info
                  </i>
                  {t("Add multiple aliases that users can say to trigger this command")}
                </small>
              </div>

              {/* URL */}
              <div className="form-group mb-4">
                <Form.Label className="fw-semibold mb-2">
                  {t("Target URL")}
                  <span className="text-danger ms-1">*</span>
                </Form.Label>
                <InputGroup>
                  <InputGroup.Text className="bg-light border-end-0">
                    <i className="material-symbols-outlined" style={{ fontSize: 20, color: '#6c757d' }}>
                      link
                    </i>
                  </InputGroup.Text>
                  <Form.Control
                    placeholder={t("Enter the target URL (e.g., https://example.com/page)")}
                    aria-label={t("URL")}
                    type="url"
                    className="border-start-0"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setVoiceCommandURL(e.target.value)}
                    value={voiceCommandURL}
                    isInvalid={!!errors.voiceCommandURL}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.voiceCommandURL}
                  </Form.Control.Feedback>
                </InputGroup>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="d-flex align-items-center justify-content-center gap-3 mt-4 pt-2">
              <Button
                variant="primary"
                type="submit"
                className="py-2 rounded-3 fw-semibold"
                style={{ fontSize: 15 }}
              >
                {editingVoiceCommand ? (
                  <span className="d-flex align-items-center justify-content-center gap-2">
                    <i className="material-symbols-outlined" style={{ fontSize: 18 }}>save</i>
                    {t('Update Command')}
                  </span>
                ) : (
                  <span className="d-flex align-items-center justify-content-center gap-2">
                    <i className="material-symbols-outlined" style={{ fontSize: 18 }}>add_circle</i>
                    {t('Create Command')}
                  </span>
                )}
              </Button>
              <Button
                variant="outline-secondary"
                onClick={handleCloseVoiceCommandPopup}
                className="px-4 py-2 rounded-3"
              >
                {t('Cancel')}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteVoiceCommandPopup}
        onHide={handleCloseDeleteVoiceCommandPopup}
        size="lg"
        backdrop="static"
        centered
        className="voice-command-delete-modal"
      >
        <Modal.Header closeButton className="border-0 pt-4 px-4">
          <Modal.Title className="fs-5 fw-semibold">
            <div className="d-flex align-items-center gap-2">
              <div className="bg-danger bg-opacity-10 rounded-circle p-2 d-flex align-items-center justify-content-center" style={{ width: 40, height: 40 }}>
                <i className="material-symbols-outlined text-danger">delete_forever</i>
              </div>
              <span>{t("Delete Voice Command")}</span>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4 pb-3">
          <p className="mb-0 text-muted">
            {t("Are you sure you want to delete this voice navigation command? This action cannot be undone.")}
          </p>
        </Modal.Body>
        <Modal.Footer className="border-0 pb-4 px-4 pt-0">
          <Button
            variant="danger"
            onClick={handleDeleteVoiceCommand}
            className="px-4"
          >
            <i className="material-symbols-outlined me-1" style={{ fontSize: 16 }}>delete</i>
            {t("Delete")}
          </Button>
          <Button
            variant="light"
            onClick={handleCloseDeleteVoiceCommandPopup}
          >
            {t("Cancel")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* All Aliases Modal */}
      <Modal
        show={showAliasModal}
        onHide={() => setShowAliasModal(false)}
        size="lg"
        backdrop="static"
        centered
        className="aliases-modal"
      >
        <Modal.Header closeButton className="border-0 pt-4 px-4">
          <Modal.Title className="fs-5 fw-semibold">
            <div className="d-flex align-items-center gap-2">
              <i className="material-symbols-outlined text-primary">list_alt</i>
              {t("All Aliases")}
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4 pb-4">
          <div className="aliases-list-container">
            {selectedAliases.map((alias, index) => (
              <div key={index} className="alias-item d-flex align-items-center gap-2 py-2 border-bottom">
                <i className="material-symbols-outlined text-muted" style={{ fontSize: 16 }}>
                  alternative_email
                </i>
                <span className="flex-grow-1">{alias}</span>
              </div>
            ))}
          </div>
        </Modal.Body>
      </Modal>

      <style>{`
        .voice-command-card {
          transition: all 0.3s ease;
          cursor: pointer;
        }
        
        .voice-command-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 24px rgba(0,0,0,0.1) !important;
        }
        
        .cursor-pointer {
          cursor: pointer;
        }
        
        .alias-wrapper {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
        }
        
        .empty-state {
          background: linear-gradient(135deg, #f8f9fa 0%, #f1f3f5 100%);
          border-radius: 1rem;
          padding: 2rem !important;
        }
        
        .form-group label {
          font-size: 14px;
          color: #212529;
        }
        
        .input-group-text {
          background-color: #f8f9fa;
        }
        
        .alias-input-group {
          transition: all 0.2s ease;
        }
      `}</style>
    </>
  );
};

export default VoiceCommandPage;